/**
 * Returns today's date as ISO string (YYYY-MM-DD) in UTC.
 * Used to validate ETA dates are not in the past.
 */
function getTodayISO() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Returns true if the given ISO date string is strictly before today.
 */
function isDateInPast(isoDate) {
  return isoDate < getTodayISO();
}

/**
 * Reads rawEta from localStorage with staleness check.
 * Returns the data only if it was written less than 5 minutes ago.
 * Returns empty array if data is stale, missing, or has no timestamp.
 */
function getLocalStorageEta() {
  var MAX_AGE_MS = 5 * 60 * 1000; // 5 minutes
  try {
    var timestamp = parseInt(localStorage.getItem('rawEtaTimestamp') || '0', 10);
    var age = Date.now() - timestamp;
    if (age > MAX_AGE_MS) {
      return [];
    }
    return JSON.parse(localStorage.getItem('rawEta') || '[]');
  } catch (e) {
    return [];
  }
}

/**
 * Fetches fresh dispatch ETA data by loading product page HTML and
 * extracting the #dispatch-eta-data JSON that Liquid renders server-side.
 * This is the fallback when localStorage is stale or empty.
 *
 * @param {string[]} handles - Array of unique product handles from cart items
 * @returns {Array} - Array of {variant_id, ranges} objects (same format as rawEta)
 */
async function fetchFreshEtaFromProductPages(handles) {
  var freshEtaData = [];
  var fetchPromises = handles.map(function (handle) {
    return fetch('/products/' + handle, { cache: 'no-store' })
      .then(function (res) {
        if (!res.ok) return null;
        return res.text();
      })
      .then(function (html) {
        if (!html) return;
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var etaScript = doc.querySelector('#dispatch-eta-data');
        if (!etaScript) return;

        var parsed = null;
        try {
          parsed = JSON.parse(etaScript.textContent);
          if (typeof parsed === 'string') parsed = JSON.parse(parsed);
        } catch (e) {
          return;
        }
        if (!parsed) return;

        // Metafield is an object keyed by SKU: { "SKU-1": {variant_id, ranges}, ... }
        // Convert to array format matching rawEta
        for (var sku in parsed) {
          if (parsed.hasOwnProperty(sku) && parsed[sku] && parsed[sku].variant_id) {
            freshEtaData.push(parsed[sku]);
          }
        }
      })
      .catch(function (err) {
      });
  });

  await Promise.all(fetchPromises);

  return freshEtaData;
}

function formatDateWithSuffix(isoDate) {
  const [y, m, d] = isoDate.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d)); // avoid timezone shifts

  const day = date.getUTCDate();
  const year = date.getUTCFullYear();

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[date.getUTCMonth()];

  const suffix =
    day % 100 >= 11 && day % 100 <= 13
      ? 'th'
      : day % 10 === 1
        ? 'st'
        : day % 10 === 2
          ? 'nd'
          : day % 10 === 3
            ? 'rd'
            : 'th';

  return `${day}${suffix} ${month} ${year}`;
}

function getProductEtaDate(variantId, qty, metaData) {
  for (const variant of metaData) {
    if (variant.variant_id == variantId) {
      const ranges = variant.ranges;
      for (const r of ranges) {
        if (qty >= r.min && qty <= r.max) {
          return r.eta;
        }
      }
    }
  }
  return null;
}

let setCartAttribute = (Eta) => {
  fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      attributes: {
        ETA: Eta,
      },
    }),
  })
    .then((res) => res.json())
    .then((data) => {
      let etaText = document.querySelector('#cartEtaDisplay span');
      if (etaText) {
        etaText.innerText = data.attributes.ETA;
      }
      let etaDrawerText = document.querySelector('#cartDrawerEtaDisplay span');
      if (etaDrawerText) {
        etaDrawerText.innerText = data.attributes.ETA;
      }
    });
};

let updateEta = async () => {
  try {
    const cartRes = await fetch('/cart.js');
    const cartData = await cartRes.json();

    const cartItems = cartData.items.map(function (item) {
      return {
        variant_id: item.variant_id,
        quantity: item.quantity,
        handle: item.handle,
      };
    });

    if (cartItems.length === 0) {
      return;
    }

    // Step 1: Try localStorage (fast path)
    let etaData = getLocalStorageEta();

    // Step 2: Fetch fresh data for any items missing from localStorage
    var uniqueHandles = [];
    cartItems.forEach(function (item) {
      const isCached = etaData.some(function (cached) { return cached.variant_id == item.variant_id; });
      if (!isCached && item.handle && uniqueHandles.indexOf(item.handle) === -1) {
        uniqueHandles.push(item.handle);
      }
    });

    if (uniqueHandles.length > 0) {
      const freshData = await fetchFreshEtaFromProductPages(uniqueHandles);
      etaData = etaData.concat(freshData);

      // Save updated data to localStorage
      try {
        localStorage.setItem('rawEta', JSON.stringify(etaData));
        localStorage.setItem('rawEtaTimestamp', Date.now().toString());
      } catch (e) { }
    }

    // Step 3: Calculate ETA dates for all cart items
    const totalDatesFound = [];
    cartItems.forEach(function (item) {
      var datefound = getProductEtaDate(item.variant_id, item.quantity, etaData);
      if (datefound) {
        totalDatesFound.push(datefound);
      }
    });

    // Step 4: Find the latest date and set it as cart attribute
    if (totalDatesFound.length > 0) {
      const latestDate = totalDatesFound.reduce(
        (max, curr) => (new Date(curr) > new Date(max) ? curr : max),
        totalDatesFound[0]
      );

      // Guard: if dispatch date is in the past, show "Call Us For Details" instead
      if (isDateInPast(latestDate)) {
        if (window.__enableCallUsForDetails) {
          setCartAttribute('Call Us For Details');
          return;
        }
      }

      let formatedDate = formatDateWithSuffix(latestDate);
      setCartAttribute(formatedDate);
    } else {
      // No ETA dates found for any cart item — show "Call Us For Details"
      if (window.__enableCallUsForDetails) {
        setCartAttribute('Call Us For Details');
      }
    }
  } catch (err) {
  }
};


//select all cart-product-info elements and get the data-selected-variant-id 
let getSelectedVariantId = () => {
  const cartProductInfoElements = document.querySelectorAll('.cart-product-info');
  cartProductInfoElements.forEach(element => {
    const selectedVariantId = element.dataset.selectedVariantId;
    updateCartItemsEta(selectedVariantId);
  });
};

const updateCartItemsEta = (selectedVariantId) => {
  let etaData = getLocalStorageEta();
  let found = false;
  etaData.forEach(item => {
    if (item.variant_id == selectedVariantId) {
      found = true;
      updateTargetCartItemEta(selectedVariantId, item.ranges);
    }
  });
  // No ETA data found for this variant at all — show "Call Us For Details"
  if (!found && window.__enableCallUsForDetails) {
    let targetDiv = document.querySelector(`[data-selected-variant-id="${selectedVariantId}"]`);
    if (targetDiv) {
      targetDiv.querySelector('.cart-item-eta-date-text').innerText = 'Est. Dispatch: Call Us For Details';
    }
  }
}

const updateTargetCartItemEta = (selectedVariantId, ranges) => {
  let targetDiv = document.querySelector(`[data-selected-variant-id="${selectedVariantId}"]`);
  let closestQtyInput = targetDiv.parentElement.querySelector('.cart-qty');
  let currentQty = parseInt(closestQtyInput.value);
  let etaDateText = cartItemEtaDate(selectedVariantId, currentQty, ranges);
  if (targetDiv && etaDateText) {
    // Guard: if date is in the past, show "Call Us For Details" instead
    if (isDateInPast(etaDateText)) {
      if (window.__enableCallUsForDetails) {
        targetDiv.querySelector('.cart-item-eta-date-text').innerText = 'Est. Dispatch: Call Us For Details';
        return;
      }
    }
    let convertedDate = formatDateWithSuffix(etaDateText);
    targetDiv.querySelector('.cart-item-eta-date-text').innerText = 'Est. Dispatch: ' + convertedDate;
  } else if (targetDiv && window.__enableCallUsForDetails) {
    // No ETA date found for this item (no matching range) — show "Call Us For Details"
    targetDiv.querySelector('.cart-item-eta-date-text').innerText = 'Est. Dispatch: Call Us For Details';
  }
}

const cartItemEtaDate = (selectedVariantId, currentQty, ranges) => {
  for (const r of ranges) {
    if (currentQty >= r.min && currentQty <= r.max) {
      return r.eta;
    }
  }
  return null;
}

let etaTimeout;
let getSelectedVariantIdTimeout;
window.addEventListener('cart:updated', function () {
  clearTimeout(etaTimeout);
  clearTimeout(getSelectedVariantIdTimeout);
  etaTimeout = setTimeout(async function () {
    await updateEta();
    getSelectedVariantId();
  }, 600);
});

window.addEventListener('cart-drawer:added', function () {
  clearTimeout(getSelectedVariantIdTimeout);
  getSelectedVariantIdTimeout = setTimeout(async function () {
    await updateEta();
    getSelectedVariantId();
  }, 1000);
});

window.addEventListener('cart-drawer:updated', async function () {
  await updateEta();
  getSelectedVariantId();
});

window.addEventListener('DOMContentLoaded', async function () {
  await updateEta();
  getSelectedVariantId();
});